import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  Check, X, ArrowRight, Star, Info, Clock, FileText,
  Target, TrendingUp, Users, Zap, MessageSquare, Sparkles,
  BarChart3, PenTool, Search, Shield, Heart, CreditCard
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// URLs des captures d'écran des témoignages LinkedIn
const TESTIMONIAL_SCREENSHOTS = {
  alexis: 'https://customer-assets.emergentagent.com/job_f8bb1ad7-5164-4c11-b0a5-dc84a9210d0e/artifacts/7ow8looh_alexis.png',
  valerie: 'https://customer-assets.emergentagent.com/job_f8bb1ad7-5164-4c11-b0a5-dc84a9210d0e/artifacts/qx2p4yz0_valerie.png'
};

// Détection automatique du domaine pour la production
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'boost-strategia.com' || window.location.hostname === 'www.boost-strategia.com') {
      return 'https://boost-strategia.com';
    }
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};
const API = `${getApiUrl()}/api`;

const Landing = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Gère le clic sur "Passer à la version payante"
  const handleDirectCheckout = async (plan) => {
    const token = localStorage.getItem('token');
    
    // Si non connecté, rediriger vers l'authentification avec retour vers pricing
    if (!token) {
      navigate('/auth', { state: { returnTo: '/pricing', selectedPlan: plan } });
      return;
    }

    // Si connecté, lancer directement le checkout Stripe
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/payments/checkout/session`,
        {
          plan,
          promo_code: null,
          origin_url: window.location.origin
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la session de paiement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <Zap className="w-6 h-6 text-primary" />
            <span className="font-heading font-bold text-lg sm:text-xl">Boost-StrategIA</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/auth">
              <Button variant="ghost" size="sm" className="text-sm">
                Connexion
              </Button>
            </Link>
            <Link to="/essai-gratuit">
              <Button size="sm" className="shadow-glow-sm text-sm">
                Essai gratuit
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-28 pb-16 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-primary font-medium mb-4 text-sm sm:text-base">
              Stratégie marketing guidée par l'IA
            </p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6 leading-tight">
              Tu sais ce que tu vaux.<br />
              <span className="text-primary italic">Le problème, c'est que tes clients, eux, ne le voient pas encore.</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted max-w-3xl mx-auto mb-4">
              Boost-StrategIA <strong className="text-foreground">guide les coachs, formateurs, infopreneurs et thérapeutes</strong> pas à pas pour construire une stratégie marketing complète, sur mesure, en quelques heures. De ton profil client idéal jusqu'à tes emails de vente.
            </p>
            <p className="text-lg sm:text-xl max-w-3xl mx-auto mb-8">
              <strong className="text-foreground">Pas des conseils génériques. Ta stratégie. Tes mots. Tes clients.</strong>
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-6">
              <Link to="/essai-gratuit">
                <Button size="lg" className="shadow-glow-md text-base px-8">
                  Démarrer mon essai gratuit
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            
            <p className="text-sm text-muted">
              7 jours gratuits · sans engagement · aucune carte bancaire
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-3 gap-4 sm:gap-8 mt-12 max-w-2xl mx-auto"
          >
            <div className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-primary">+120%</p>
              <p className="text-xs sm:text-sm text-muted">de CA sur un lancement à 2 500€</p>
            </div>
            <div className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-primary">51</p>
              <p className="text-xs sm:text-sm text-muted">étapes guidées de A à Z</p>
            </div>
            <div className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-primary">79€</p>
              <p className="text-xs sm:text-sm text-muted">vs 3 000€/jour un consultant</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 px-4 sm:px-6 bg-card/30">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-primary text-sm font-medium mb-2">Le vrai problème</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold mb-4">
              Tu travailles. Tu produis.<br />
              Et pourtant <span className="text-primary">quelque chose bloque</span>.
            </h2>
            <p className="text-lg text-muted max-w-2xl mx-auto">
              Ce n'est pas ton offre qui est mauvaise. C'est que personne ne t'a encore aidé à la mettre en face des bonnes personnes, avec les bons mots, au bon moment.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: '🌫️', title: 'Stratégie floue', desc: "Tu avances à l'instinct. Tu publies, tu prospectes, tu ajustes. Mais sans cap défini, tu tournes en rond." },
              { icon: '🎯', title: 'Positionnement trop large', desc: "Tu parles à tout le monde - donc tu ne parles vraiment à personne. Résultat : un message qui glisse sans accrocher." },
              { icon: '📉', title: 'Ventes irrégulières', desc: "Des mois corrects, des mois à serrer la ceinture. Pas de système. Pas de prévisibilité. Juste de l'anxiété." },
              { icon: '🔍', title: 'Concurrents mal identifiés', desc: "Tu ne sais pas vraiment ce qui te différencie - ni pourquoi un prospect choisirait toi plutôt qu'un autre." },
              { icon: '✍️', title: 'Copywriting qui ne convertit pas', desc: "Tu écris. Tu peaufines. Mais tes pages de vente et tes emails ne déclenchent pas ce passage à l'acte." },
              { icon: '💸', title: 'Formations génériques achetées', desc: "Tu as investi dans des méthodes qui t'apprennent les principes - mais aucune ne te dit où ça coince dans TON business." }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-card border border-border rounded-xl p-6"
              >
                <span className="text-3xl mb-3 block">{item.icon}</span>
                <h3 className="font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-muted">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-8 bg-primary/10 border border-primary/30 rounded-xl p-6 text-center"
          >
            <p className="text-sm sm:text-base">
              💡 <strong>La formation à 15 000€ est faite pour 500 personnes en même temps.</strong><br />Elle ne peut pas descendre dans les spécificités de ton offre, ton marché, ton positionnement.<br />Ce n'est pas un défaut, c'est une limite structurelle du modèle.<br />Boost-StrategIA construit <strong className="text-primary">ta stratégie avec tes données</strong>.<br />Pas un modèle générique.
            </p>
          </motion.div>

          {/* CTA after Problem Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-10 text-center"
          >
            <Link to="/essai-gratuit">
              <Button size="lg" className="shadow-glow-md">
                Découvrir la solution
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-primary text-sm font-medium mb-2">Ce que tu repars avec</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold mb-4">
              Une stratégie complète.<br />
              <span className="text-primary">Dans tes mains. Prête à exécuter.</span>
            </h2>
            <p className="text-lg text-muted max-w-2xl mx-auto">
              Boost-StrategIA ne te donne pas des "pistes à explorer".<br />Il génère des livrables concrets, exportables en PDF, basés sur tes réponses.
            </p>
          </motion.div>

          <div className="space-y-6">
            {[
              { num: '01', title: 'Ton Profil Client Idéal complet', desc: "23 étapes pour cartographier ton client idéal avec une précision chirurgicale - ses douleurs profondes, ses déclencheurs d'achat, ses objections, son langage exact. La base de tout le reste.", badge: '23 étapes guidées', icon: Users },
              { num: '02', title: 'Ton analyse concurrentielle structurée', desc: "Qui sont tes vrais concurrents. Ce qu'ils font bien. Ce qu'ils ne font pas. Et les angles où tu peux t'imposer sans te battre sur le prix.", badge: 'Différenciation actionnable', icon: Search },
              { num: '03', title: 'Ton positionnement et tes 3 Big Idea', desc: 'Ton angle différenciant. Le concept fort qui va traverser toute ta communication. La phrase qui fait que ton prospect se dit "c\'est exactement pour moi".', badge: 'Angle différenciant unique', icon: Target },
              { num: '04', title: 'Tes promesses et sous-promesses et tes emails de vente', desc: "Des emails qui convertissent, rédigés avec ton ton, pour ton audience, dans le bon ordre. Plus à écrire dans le vide - tu as une structure qui a fait ses preuves.", badge: 'Prêt à envoyer', icon: PenTool }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-card border border-border rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row gap-6"
              >
                <div className="flex-shrink-0 text-center sm:text-left">
                  <span className="text-4xl sm:text-5xl font-bold text-primary/30">{item.num}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <item.icon className="w-5 h-5 text-primary" />
                    <h3 className="text-xl font-bold">{item.title}</h3>
                  </div>
                  <p className="text-muted mb-4">{item.desc}</p>
                  <span className="inline-block bg-primary/10 border border-primary/30 rounded-full px-3 py-1 text-xs text-primary">
                    {item.badge}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA after What You Get Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <Link to="/essai-gratuit">
            <Button size="lg" className="shadow-glow-md">
              Obtenir ma stratégie complète
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </motion.div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 px-4 sm:px-6 bg-card/30">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-primary text-sm font-medium mb-2">Comment ça marche</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold mb-4">
              Pas de courbe d'apprentissage.<br />
              <span className="text-primary">Tu réponds. Boost-StrategIA construit.</span>
            </h2>
            <p className="text-lg text-muted max-w-2xl mx-auto">
              Si tu sais remplir un formulaire, tu sais utiliser Boost-StrategIA.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { num: 1, title: 'Tu choisis ton étude et tu réponds aux questions guidées', desc: "L'outil t'accompagne étape par étape. Chaque question est conçue pour extraire ce qui compte vraiment dans ton business - sans jargon, sans technicité.", badge: '⏱ À ton rythme' },
              { num: 2, title: "L'IA analyse en s'appuyant sur ma méthodologie en 51 étapes et ma base de connaissance, fruits de + de 35 ans d'expérience", desc: "Pas ChatGPT en mode libre. Une méthodologie séquencée, construite par des experts marketing, qui traite tes données dans le bon ordre pour produire une stratégie cohérente.", badge: '⚡ Traitement immédiat' },
              { num: 3, title: 'Tu reçois ta stratégie complète, exportable en PDF', desc: "Positionnement, angles différenciants, profil client détaillé, copywriting - tout est là, structuré, prêt à exécuter. Pas des idées en vrac. Un plan.", badge: '📄 Export PDF inclus' },
              { num: 4, title: "Tu appliques - et tu n'es pas seul·e", desc: "2 coachings live par mois avec Valérie (valeur 120€) pour valider ta stratégie, poser tes questions et ajuster en temps réel. Boost-StrategIA construit, l'humain affine.", badge: '👁 2 coachings live/mois inclus' }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-card border border-border rounded-xl p-6"
              >
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                  <span className="font-bold text-primary">{item.num}</span>
                </div>
                <h3 className="font-bold mb-3 text-lg">{item.title}</h3>
                <p className="text-sm text-muted mb-4">{item.desc}</p>
                <span className="text-xs text-primary">{item.badge}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA after How It Works Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <Link to="/essai-gratuit">
            <Button size="lg" className="shadow-glow-md">
              Commencer maintenant
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </motion.div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-primary text-sm font-medium mb-2">Pourquoi Boost-StrategIA</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold mb-4">
              La méthode des consultants experts.<br />
              <span className="text-primary">À 1% du prix.</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="overflow-x-auto"
          >
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-4 text-sm font-medium">Critère</th>
                  <th className="text-center p-4 text-sm font-medium text-primary">Boost-StrategIA</th>
                  <th className="text-center p-4 text-sm font-medium text-muted">ChatGPT seul</th>
                  <th className="text-center p-4 text-sm font-medium text-muted">Formation</th>
                  <th className="text-center p-4 text-sm font-medium text-muted">Consultant</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {[
                  { label: 'Stratégie guidée étape par étape', boost: '✓ 51 étapes', gpt: '✗', formation: '~ Générique', consultant: '✓' },
                  { label: 'Personnalisé à TON business', boost: '✓ Tes données', gpt: '~ Si bon prompt', formation: '✗', consultant: '✓' },
                  { label: 'Analyse concurrentielle', boost: '✓ Incluse', gpt: '~', formation: '✗', consultant: '✓' },
                  { label: 'Copywriting & emails générés', boost: '✓ Inclus', gpt: '~', formation: '✗', consultant: '✓' },
                  { label: 'Coaching live humain inclus', boost: '✓ 2×/mois', gpt: '✗', formation: '✗', consultant: '✓' },
                  { label: 'Résultats chiffrés documentés', boost: '✓ +80-120% CA', gpt: '✗', formation: '✗', consultant: '~' },
                  { label: 'Prix accessible', boost: '✓ 79€/mois', gpt: '✓ 20$/mois', formation: '✗ 1 000-15 000€', consultant: '✗ 1 500-5 000€/j' }
                ].map((row, i) => (
                  <tr key={i} className="border-b border-border/50">
                    <td className="p-4 font-medium">{row.label}</td>
                    <td className="p-4 text-center text-primary">{row.boost}</td>
                    <td className="p-4 text-center text-muted">{row.gpt}</td>
                    <td className="p-4 text-center text-muted">{row.formation}</td>
                    <td className="p-4 text-center text-muted">{row.consultant}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>

        {/* CTA after Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <Link to="/essai-gratuit">
            <Button size="lg" className="shadow-glow-md">
              Tester gratuitement pendant 7 jours
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </motion.div>
      </section>

      {/* Testimonials Section - RÉINTÉGRATION DE L'EXISTANT */}
      <section className="py-16 px-4 sm:px-6 bg-card/30" data-testid="testimonials-section">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-primary text-sm font-medium mb-2">Ils l'ont utilisé</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold mb-4">
              Des résultats réels.<br />
              <span className="text-primary">Des vrais business.</span>
            </h2>
            <p className="text-lg text-muted mb-6">
              Pas des promesses. Des exemples concrets, avec les chiffres qui vont avec.
            </p>
            <div className="inline-flex items-center gap-2 bg-muted/20 border border-border rounded-full px-4 py-2 text-sm">
              <Info className="w-4 h-4 text-primary" />
              <span className="text-muted">
                <span className="text-primary font-medium">Business Boosteur</span> est l'ancienne version de Boost-StrategIA
              </span>
            </div>
          </motion.div>

          {/* Video Testimonials */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-card border border-primary/30 rounded-xl p-4"
            >
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-4 h-4 text-primary fill-primary" />
                <span className="text-xs font-medium text-primary">Témoignage vidéo - Michelle DEPRE</span>
              </div>
              <div className="aspect-video bg-black/50 rounded-lg overflow-hidden relative">
                <iframe 
                  width="100%" 
                  height="100%" 
                  src="https://www.youtube-nocookie.com/embed/GAZyfjwZp7w?rel=0&modestbranding=1" 
                  title="Témoignage vidéo Michelle DEPRE"
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                  allowFullScreen
                  loading="lazy"
                  className="absolute inset-0"
                ></iframe>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-card border border-pink-500/30 rounded-xl p-4"
            >
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-4 h-4 text-pink-400 fill-pink-400" />
                <span className="text-xs font-medium text-pink-400">Témoignage audio - Sylvie Stevanovic</span>
              </div>
              <div className="aspect-video bg-black/50 rounded-lg overflow-hidden relative">
                <iframe 
                  width="100%" 
                  height="100%" 
                  src="https://www.youtube-nocookie.com/embed/pYYms2c26RQ?rel=0&modestbranding=1" 
                  title="Témoignage audio Sylvie Stevanovic"
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                  allowFullScreen
                  loading="lazy"
                  className="absolute inset-0"
                ></iframe>
              </div>
            </motion.div>
          </div>

          {/* LinkedIn Testimonials Screenshots */}
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { key: 'alexis', name: 'Alexis Rocco' },
              { key: 'valerie', name: 'Valérie Beaufay' }
            ].map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
              >
                <img 
                  src={TESTIMONIAL_SCREENSHOTS[testimonial.key]}
                  alt={`Témoignage LinkedIn de ${testimonial.name}`}
                  className="w-full h-auto"
                />
              </motion.div>
            ))}
          </div>

          {/* CTA after Testimonials */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mt-10 text-center"
          >
            <Link to="/essai-gratuit">
              <Button size="lg" className="shadow-glow-md">
                Je veux les mêmes résultats
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-primary text-sm font-medium mb-2">Les formules</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold mb-4">
              7 jours gratuits.<br />
              <span className="text-primary">Sans carte bancaire.</span>
            </h2>
            <p className="text-lg text-muted max-w-2xl mx-auto">
              Une seule vente d'une offre à 2 500€ grâce à ta nouvelle stratégie rembourse <strong className="text-foreground">31 mois d'abonnement</strong>. Le calcul est simple.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Plan Pro */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-card border border-border rounded-2xl p-8"
            >
              <h3 className="text-xl font-bold mb-2">Plan Pro</h3>
              <p className="text-sm text-muted mb-6">
                Pour les <strong>coachs, formateurs et indépendants</strong> qui veulent construire leur stratégie eux-mêmes.
              </p>
              <div className="mb-6">
                <span className="text-4xl font-bold">79€</span>
                <span className="text-muted">/mois</span>
                <p className="text-xs text-muted mt-1">ou 790€/an · économise 2 mois</p>
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  '2 études stratégiques complètes/mois',
                  '51 étapes guidées (CPI, concurrents, positionnement, Big Idea, copywriting)',
                  'Export PDF de chaque stratégie',
                  '2 coachings live/mois avec Valérie (valeur 120€)',
                  'Accès illimité 24h/24, 7j/7'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/essai-gratuit">
                <Button className="w-full shadow-glow-md">
                  Démarrer 7 jours gratuits
                </Button>
              </Link>
              <Button 
                variant="outline" 
                className="w-full mt-3 bg-blue-500/20 border-blue-500/50 text-blue-400 hover:bg-blue-500/30 hover:text-blue-300"
                onClick={() => handleDirectCheckout('pro_monthly')}
                disabled={loading}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {loading ? 'Chargement...' : 'Passer à la version payante'}
              </Button>
            </motion.div>

            {/* Plan Agency */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-card border-2 border-primary rounded-2xl p-8 relative"
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                Pour les agences & consultants
              </div>
              <h3 className="text-xl font-bold mb-2">Plan Agency</h3>
              <p className="text-sm text-muted mb-6">
                Pour les <strong>consultants et agences</strong> qui utilisent l'outil pour leurs propres clients.
              </p>
              <div className="mb-6">
                <span className="text-4xl font-bold">199€</span>
                <span className="text-muted">/mois</span>
                <p className="text-xs text-muted mt-1">ou 1 990€/an · économise 2 mois</p>
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  '5 études stratégiques complètes/mois',
                  'Idéal pour facturer la stratégie à tes clients',
                  '51 étapes guidées (toutes les fonctionnalités Pro)',
                  'Export PDF de chaque stratégie',
                  '2 coachings live/mois avec Valérie (valeur 120€)',
                  'Accès illimité 24h/24, 7j/7'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/essai-gratuit">
                <Button className="w-full shadow-glow-md">
                  Démarrer 7 jours gratuits
                </Button>
              </Link>
              <Button 
                variant="outline" 
                className="w-full mt-3 bg-blue-500/20 border-blue-500/50 text-blue-400 hover:bg-blue-500/30 hover:text-blue-300"
                onClick={() => handleDirectCheckout('agency_monthly')}
                disabled={loading}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {loading ? 'Chargement...' : 'Passer à la version payante'}
              </Button>
            </motion.div>
          </div>

          <p className="text-center text-sm text-muted mt-8">
            Sans engagement · Aucune carte bancaire requise · Annulation en 1 clic
          </p>

          {/* Solidarité animale */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mt-10 max-w-2xl mx-auto"
          >
            <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/20 rounded-xl p-6 flex flex-col md:flex-row items-center gap-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_65659477-73eb-427c-9057-d93b7f82d27e/artifacts/ytwjdp0n_slide9_mascottes.png"
                alt="Nos mascottes pour la protection animale"
                className="w-32 h-auto"
              />
              <div className="text-left">
                <p className="text-lg font-semibold text-pink-400 mb-2">
                  🐾 Ensemble pour la cause animale
                </p>
                <p className="text-muted text-sm">
                  <span className="text-white font-medium">15% de nos revenus</span> sont reversés à des associations de protection animale. 
                  En choisissant Boost-StrategIA, vous contribuez à protéger nos amis à quatre pattes.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 bg-card/30">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-primary text-sm font-medium mb-2">Questions fréquentes</p>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold">
              On répond <span className="text-primary">cash</span>.
            </h2>
          </motion.div>

          <div className="space-y-4">
            {[
              { q: "C'est quoi la différence avec ChatGPT ?", a: "ChatGPT sans structure, c'est un moteur sans châssis. Boost-StrategIA, c'est 51 étapes dans l'ordre, conçues par des experts marketing, alimentées par tes données. Résultat : une stratégie cohérente et actionnable, pas une liste d'idées en vrac." },
              { q: "En combien de temps j'obtiens ma stratégie ?", a: "Une étude complète se fait en quelques heures, à ton rythme. Tu avances étape par étape, tu peux sauvegarder et reprendre. Les résultats sont disponibles immédiatement après chaque module complété." },
              { q: "Mon business est trop spécifique pour un outil IA ?", a: "L'IA ne remplace pas ta spécificité - elle l'amplifie. Elle traite tes réponses à travers une méthodologie experte. Plus tu es précis dans tes réponses, plus la stratégie générée est pertinente pour toi." },
              { q: "À quoi servent les coachings live ?", a: "2 fois par mois, Valérie est en live pour valider ta stratégie, répondre à tes questions, et t'aider à ajuster. Boost-StrategIA construit le plan - l'humain affine et rassure. Tu n'es jamais seul·e face à l'écran." },
              { q: "Quelle différence entre le plan Pro et Agency ?", a: "Le plan Pro permet 2 études stratégiques complètes par mois - idéal pour construire ta propre stratégie. Le plan Agency en donne 5 - conçu pour les consultants qui réalisent des études pour leurs clients et facturent ce travail." },
              { q: "Je peux annuler quand je veux ?", a: "Oui, sans engagement et sans pénalité, à tout moment. Tu restes parce que ça fonctionne - pas parce que tu es coincé·e dans un contrat." }
            ].map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                viewport={{ once: true }}
                className="bg-card border border-border rounded-xl p-6"
              >
                <h3 className="font-bold mb-2">{faq.q}</h3>
                <p className="text-sm text-muted">{faq.a}</p>
              </motion.div>
            ))}
          </div>

          {/* CTA after FAQ */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mt-10 text-center"
          >
            <Link to="/essai-gratuit">
              <Button size="lg" className="shadow-glow-md">
                Lancer mon essai gratuit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Association Section */}
      <section className="py-12 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/20 rounded-xl p-6 flex flex-col md:flex-row items-center gap-6"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_65659477-73eb-427c-9057-d93b7f82d27e/artifacts/ytwjdp0n_slide9_mascottes.png"
              alt="Nos mascottes pour la protection animale"
              className="w-32 h-auto"
            />
            <div className="text-left">
              <p className="text-lg font-semibold text-pink-400 mb-2">
                🐾 Ensemble pour la cause animale
              </p>
              <p className="text-muted text-sm">
                <span className="text-white font-medium">15% de nos revenus</span> sont reversés à des associations de protection animale. 
                En choisissant Boost-StrategIA, vous contribuez à protéger nos amis à quatre pattes.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4 sm:px-6 bg-gradient-to-b from-background to-card/50">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold mb-6">
              Ta stratégie marketing <span className="text-primary">t'attend</span>.
            </h2>
            <p className="text-lg text-muted mb-8">
              7 jours pour tester. Zéro engagement. Et si ça ne te convient pas, tu repars sans payer un centime.
            </p>
            <Link to="/essai-gratuit">
              <Button size="lg" className="shadow-glow-lg text-lg px-10">
                Démarrer mon essai gratuit →
              </Button>
            </Link>
            <p className="text-sm text-muted mt-4">
              Sans carte bancaire · Accès immédiat · Résultats dès la première étude
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 border-t border-border">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            <span className="font-heading font-bold">Boost-StrategIA</span>
          </div>
          <div className="flex gap-6 text-sm text-muted">
            <Link to="/terms" className="hover:text-foreground transition-colors">CGV</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Confidentialité</Link>
            <a href="mailto:contact@boost-strategia.com" className="hover:text-foreground transition-colors">Contact</a>
          </div>
          <p className="text-xs text-muted">
            © 2025 Boost-StrategIA. Tous droits réservés.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
